import { createAction, createReducer, createSelector } from "@reduxjs/toolkit"
import { isEmptyFields, toSearchParams } from "helpers/form"
import { formatPaginationParams } from "helpers/pagination"
import { find, isEqual, reject } from "lodash"
import { createdFavouriteTrips, deletedFavouriteTrips } from "store/favourite-trips"
import { api } from "./helpers/api"
import { collectionSelector, modelSelector } from "./selectors"
import { LOADING_INITIAL_STATE, setFailed, setReceived, setRequested } from "modules/loader-watchers/helpers/loading"
import { PAGINATION_INITIAL_STATE } from "./constants"

const EMPTY_DRAFT_FIELDS = [
  "title",
  "description",
  "start_date",
  "end_date",
  "max_guests",
  "min_guests",
  "photos",
  "skill_level",
  "bring_items",
  "included_items",
  "target_fish_species",
  "fishing_activity_type",
  "fishing_ecosystem"
]

// ACTIONS
export const setCalendarDates = createAction("trips/setCalendarDates")
const requested = createAction("trips/requested")
const received = createAction("trips/received")
const failed = createAction("trips/failed")
const cleaned = createAction("trips/cleaned")
const requestedTrip = createAction("trips/requestedTrip")
export const receivedTrip = createAction("trips/receivedTrip")
const failedTrip = createAction("trips/failedTrip")
const createdTrip = createAction("trips/createdTrip")
const updatedTrip = createAction("trips/updatedTrip")
const deletedTrip = createAction("trips/deletedTrip")
const cleanedTrip = createAction("trips/cleanedTrip")
const requestedFishSpecies = createAction("trips/requestedFishSpecies")
const receivedFishSpecies = createAction("trips/receivedFishSpecies")
const failedFishSpecies = createAction("trips/failedFishSpecies")
const requestedSuggestCategory = createAction("trips/requestedSuggestCategory")
const receivedSuggestCategory = createAction("trips/receivedSuggestCategory")
const failedSuggestCategory = createAction("trips/failedSuggestCategory")
const requestedFilterOptions = createAction("trips/requestedFilterOptions")
const receivedFilterOptions = createAction("trips/receivedFilterOptions")
const failedFilterOptions = createAction("trips/failedFilterOptions")
export const updatedAvailableDays = createAction("trips/updatedAvailableDays")
export const updatedAvailableHours = createAction("trips/updatedAvailableHours")
export const updatedSeasonal = createAction("trips/updatedSeasonal")
const searchRequested = createAction("trips/searchRequested")
const searchReceived = createAction("trips/searchReceived")
const searchFailed = createAction("trips/searchFailed")

const getCategoriesRequest = createAction("trips/getCategoriesRequest")
const getCategoriesReceived = createAction("trips/getCategoriesReceived")
const getCategoriesFailed = createAction("trips/getCategoriesFailed")

const getTripCategoriesRequest = createAction("trips/getTripCategoriesRequest")
const getTripCategoriesReceived = createAction("trips/getTripCategoriesReceived")
const getTripCategoriesFailed = createAction("trips/getTripCategoriesFailed")

export const CLEAR_ALL_FILTERS = "CLEAR_ALL_FILTERS"

export const clearAllFilters = () => ({ type: CLEAR_ALL_FILTERS })

export const setPriceRange = createAction("trips/setPriceRange")
export const setLanguage = createAction("trips/setLanguage")
export const setSkillLevel = createAction("trips/setSkillLevel")
export const setExperiences = createAction("trips/setExperiences")
export const setOther = createAction("trips/setOther")
export const setGuests = createAction("trips/setGuests")
export const setDates = createAction("trips/setDates")
export const setSort = (payload) => ({ type: "SET_SORT", payload })
export const setGuestCount = createAction("trips/setGuestCount")

// REDUCER
const initialState = {
  ...LOADING_INITIAL_STATE,
  items: [],
  searchItems: [],
  searchLoading: false,
  searchError: null,
  fish_species: {
    ...LOADING_INITIAL_STATE,
    items: []
  },
  suggested_categories: {
    ...LOADING_INITIAL_STATE,
    items: []
  },
  filter_options: {
    ...LOADING_INITIAL_STATE,
    lists: {}
  },
  metadata: {
    ...PAGINATION_INITIAL_STATE
  },
  trip: {
    ...LOADING_INITIAL_STATE,
    id: null,
    title: null,
    description: null,
    status: "",
    fishing_activity_type: null,
    trip_length: "",
    min_guests: null,
    max_guests: null,
    seasonal: false,
    start_date: null,
    end_date: null,
    recurring_yearly: false,
    available_date_ranges: [],
    pricing_type: null,
    price_per_person: 0,
    flat_rate: 0,
    minimum_rate: 0,
    minimum_rate_person_count: 0,
    rate_per_additional_person: 0,
    latitude: null,
    longitude: null,
    available_days: {
      friday: false,
      monday: false,
      saturday: false,
      sunday: false,
      thursday: false,
      tuesday: false,
      wednesday: false
    },
    bring_items: [],
    included_items: [],
    target_fish_species: [],
    photos: [],
    media: [],
    skill_level: null,
    alcohol_allowed: false,
    accessibility_enabled: false,
    fishing_ecosystem: null,
    wizard_completed: null,
    wizard_step: "",
    existing_draft_booking: null,
    boat_ids: []
  },
  categories: {
    loading: false,
    error: null,
    data: {}
  },
  tripCategories: {
    loading: false,
    error: null,
    data: {}
  },
  priceRange: [],
  language: [],
  skillLevel: [],
  experiences: [],
  other: [],
  guests: { adults: 0, children: 0 },
  calendar: { from_date: "", to_date: "" },
  sort: { price_sort: "", distance_sort: "asc" }
}

// HELPERS
const isEmptyDraft = (item) => isEmptyFields(item, EMPTY_DRAFT_FIELDS) && isEqual(item.available_days, initialState.trip.available_days)

const tripsReducer = createReducer(initialState, {
  [requested.type]: (state) => {
    setRequested(state)
  },
  [received.type]: (state, action) => {
    setReceived(state)
    state.items = action.props.reset ? action.payload.data : state.items.concat(action.payload.data)
    Object.assign(state.metadata, action.payload.metadata)
  },
  [failed.type]: (state) => {
    setFailed(state)
  },
  [cleaned.type]: () => initialState,
  [requestedTrip.type]: (state) => {
    setRequested(state.trip)
  },
  [receivedTrip.type]: (state, action) => {
    setReceived(state.trip)
    Object.assign(state.trip, action.payload.data || action.payload)
    if (action.payload.metadata) Object.assign(state.metadata, action.payload.metadata)
  },
  [createdTrip.type]: (state, action) => {
    setReceived(state.trip)
    const trip = action.payload.data || action.payload
    state.items = [trip, ...state.items]
    Object.assign(state.trip, trip)
  },
  [updatedTrip.type]: (state, action) => {
    setReceived(state.trip)
    const trip = find(state.items, ["id", action.payload.data.id || action.payload.id]) || {}
    Object.assign(trip, action.payload.data || action.payload)
    Object.assign(state.trip, action.payload.data || action.payload)
    state.statusUpdated = !state.statusUpdated
  },
  [deletedTrip.type]: (state, action) => {
    setReceived(state.trip)
    state.items = state.items.filter((trip) => trip.id !== action.payload.id)
  },
  [failedTrip.type]: (state) => {
    setFailed(state.trip)
  },
  [cleanedTrip.type]: (state) => {
    state.trip = initialState.trip
  },
  [requestedFishSpecies.type]: (state) => {
    setRequested(state.fish_species)
  },
  [receivedFishSpecies.type]: (state, action) => {
    setReceived(state.fish_species)
    state.fish_species.items = action.payload.data
  },
  [failedFishSpecies.type]: (state) => {
    setFailed(state.fish_species)
  },
  [requestedSuggestCategory.type]: (state) => {
    setRequested(state.fish_species)
  },
  [receivedSuggestCategory.type]: (state, action) => {
    setReceived(state.fish_species)
    state.fish_species.items = action.payload.data
  },
  [failedSuggestCategory.type]: (state) => {
    setFailed(state.fish_species)
  },
  [requestedFilterOptions.type]: (state) => {
    setRequested(state.filter_options)
  },
  [receivedFilterOptions.type]: (state, action) => {
    setReceived(state.filter_options)
    state.filter_options.lists = action.payload
  },
  [failedFilterOptions.type]: (state) => {
    setFailed(state.filter_options)
  },
  [createdFavouriteTrips.type]: (state, action) => {
    const trip = find(state.items, ["id", action.payload.favoritable_id])
    if (trip) trip.favorite_id = action.payload.id
    if (state.trip.id) state.trip.favorite_id = action.payload.id
  },
  [deletedFavouriteTrips.type]: (state, action) => {
    const trip = find(state.items, ["favorite_id", action.payload.id])
    if (trip) trip.favorite_id = null
    if (state.trip.id) state.trip.favorite_id = null
  },
  [updatedAvailableDays.type]: (state, action) => {
    state.trip.available_days = action.payload
  },
  [updatedAvailableHours.type]: (state, action) => {
    state.trip.available_hours = action.payload
  },
  [updatedSeasonal.type]: (state, action) => {
    state.trip.seasonal = action.payload
  },
  [searchRequested.type]: (state) => {
    state.searchLoading = true
    state.searchError = null
  },
  [searchReceived.type]: (state, action) => {
    state.searchLoading = false
    state.searchItems = action.payload.data
    // Update metadata with search results metadata
    if (action.payload.metadata) {
      Object.assign(state.metadata, action.payload.metadata)
    }
  },
  [searchFailed.type]: (state, action) => {
    state.searchLoading = false
    state.searchError = action.error || action.payload
  },
  [getCategoriesRequest.type]: (state) => {
    state.categories.loading = true
  },
  [getCategoriesReceived.type]: (state, action) => {
    state.categories.loading = false
    state.categories.data = action.payload.data
  },
  [getCategoriesFailed.type]: (state, action) => {
    state.categories.loading = false
    state.categories.data = []
    state.categories.error = action.payload
  },
  [getTripCategoriesRequest.type]: (state) => {
    state.tripCategories.loading = true
  },
  [getTripCategoriesReceived.type]: (state, action) => {
    state.tripCategories.loading = false
    state.tripCategories.data = action.payload.data
  },
  [getTripCategoriesFailed.type]: (state, action) => {
    state.tripCategories.loading = false
    state.tripCategories.data = []
    state.tripCategories.error = action.payload
  },
  [CLEAR_ALL_FILTERS]: (state) => {
    return {
      ...state,
      priceRange: [],
      language: [],
      skillLevel: [],
      experiences: [],
      other: []
    }
  },
  [setPriceRange]: (state, action) => {
    state.priceRange = action.payload
  },
  [setLanguage]: (state, action) => {
    state.language = action.payload
  },
  [setSkillLevel]: (state, action) => {
    state.skillLevel = action.payload
  },
  [setExperiences]: (state, action) => {
    state.experiences = action.payload
  },
  [setOther]: (state, action) => {
    state.other = action.payload
  },
  [setGuests]: (state, action) => {
    state.guests = action.payload
  },
  [setDates]: (state, action) => {
    state.dates = action.payload
  },
  [setCalendarDates]: (state, action) => {
    state.calendar = action.payload
  },
  [setGuestCount]: (state, action) => {
    state.guests = action.payload
  },
  SET_SORT: (state, action) => {
    return { ...state, sort: { ...state.sort, ...action.payload } }
  }
})
export default tripsReducer

const getItems = (url, nextPage, params = {}, dispatch, getState) => {
  const state = getState()
  // Central priceRange mapping
  let mappedParams = { ...params }
  if (Array.isArray(mappedParams.priceRange)) {
    const [min_price, max_price] = mappedParams.priceRange
    mappedParams = { ...mappedParams, min_price, max_price }
    delete mappedParams.priceRange
  }
  const paginationParams = toSearchParams(formatPaginationParams(state.trips.metadata, nextPage))
  const serializedParams = toSearchParams(mappedParams, null, paginationParams)
  return dispatch(
    api({
      url,
      onStart: requested.type,
      onSuccess: received.type,
      onError: failed.type,
      params: serializedParams,
      props: { reset: !nextPage }
    })
  )
}

const getItem = (url, dispatch, additionalParams = {}) => {
  return dispatch(
    api({
      url,
      onStart: requestedTrip.type,
      onSuccess: receivedTrip.type,
      onError: failedTrip.type,
      params: { include_unavailable_dates: true, ...additionalParams }
    })
  )
}

//PUBLIC ACTIONS
export const getTrips = (nextPage, params) => (dispatch, getState) => getItems("/trips", nextPage, params, dispatch, getState)
export const getGuideTrips = (nextPage, params) => (dispatch, getState) => getItems("/guide/trips", nextPage, params, dispatch, getState)
export const getClientGuideTrips = (id, nextPage, params) => (dispatch, getState) =>
  getItems(`/client/guides/${id}/trips`, nextPage, params, dispatch, getState)

export const getTrip = (id) => (dispatch, getState) => getItem(`/trips/${id}`, dispatch)
export const getGuideTrip = (id, previewDetail, filterTripPrices = false) => (dispatch, getState) => {
  const additionalParams = filterTripPrices ? { filter_trip_prices_by_type: true } : {}
  return getItem(`/guide/trips/${id}?include_boats=${previewDetail}`, dispatch, additionalParams)
}

export const createTrip = () => (dispatch, getState) => {
  const state = getState()
  const { items } = state.trips
  const emptyDraftTrip = items.find(isEmptyDraft)

  if (emptyDraftTrip) {
    dispatch(receivedTrip(emptyDraftTrip))
    return new Promise((resolve) => resolve({ data: emptyDraftTrip }))
  }

  return dispatch(
    api({
      url: "/guide/trips",
      method: "post",
      onStart: requestedTrip.type,
      onSuccess: createdTrip.type,
      onError: failedTrip.type
    })
  )
}

export const updateTrip = (id, data) =>
  api({
    url: `/guide/trips/${id}`,
    method: "put",
    data: data,
    onSuccess: updatedTrip.type,
    onError: failedTrip.type
  })

export const deleteTrip = (id) =>
  api({
    url: `/guide/trips/${id}`,
    method: "delete",
    onStart: requestedTrip.type,
    onSuccess: deletedTrip.type,
    onError: failedTrip.type
  })

export const duplicateTrip = (id) =>
  api({
    url: `/guide/trips/${id}/duplicate`,
    method: "post",
    onStart: requestedTrip.type,
    onSuccess: createdTrip.type,
    onError: failedTrip.type
  })

export const suggestCategory = (data) =>
  api({
    url: "/guide/trips/suggest_category",
    method: "post",
    data,
    onStart: requestedSuggestCategory.type,
    onSuccess: receivedSuggestCategory.type,
    onError: failedSuggestCategory.type
  })

export const getFishSpecies = () =>
  api({
    url: `/guide/trips/fish_species`,
    onStart: requestedFishSpecies.type,
    onSuccess: receivedFishSpecies.type,
    onError: failedFishSpecies.type
  })

export const getFilterOptions = () =>
  api({
    url: `/trips/filter_options`,
    onStart: requestedFilterOptions.type,
    onSuccess: receivedFilterOptions.type,
    onError: failedFilterOptions.type
  })

export const getCategories = () =>
  api({
    url: `/trips/categories`,
    onStart: getCategoriesRequest.type,
    onSuccess: getCategoriesReceived.type,
    onError: getCategoriesFailed.type
  })

export const getTripCategories = () =>
  api({
    url: `/guide/trips/categories`,
    onStart: getTripCategoriesRequest.type,
    onSuccess: getTripCategoriesReceived.type,
    onError: getTripCategoriesFailed.type
  })
export { cleanedTrip as cleanTrip, cleaned as cleanTrips }

// SELECTORS

export const tripsWithoutEmptySelector = createSelector(collectionSelector("trips"), (trips) => reject(trips, isEmptyDraft))

export const tripLatLngSelector = createSelector(
  modelSelector("trip"),
  (trip) => trip && trip.latitude && trip.longitude && { lat: trip.latitude, lng: trip.longitude }
)

export const tripFilterOptionsSelector = (listName) =>
  createSelector(
    (state) => state.trips.filter_options.lists || {},
    (lists) => lists[listName] || []
  )

export const getGuideTripsSearch = (nextPage, params) => (dispatch, getState) => {
  const state = getState()
  const paginationParams = toSearchParams(formatPaginationParams(state.trips.metadata, nextPage))
  const serializedParams = toSearchParams(params, null, paginationParams)
  return dispatch(
    api({
      url: "/guide/trips",
      onStart: searchRequested.type,
      onSuccess: searchReceived.type,
      onError: searchFailed.type,
      params: serializedParams,
      props: { reset: !nextPage }
    })
  )
}
export const getTripList = (params = {}) => {
  return api({
    url: '/guide/trips/trip_list',
    method: 'get',
    params,
    onStart: requested.type,
    onSuccess: received.type,
    onError: failed.type
  })
}
